import classNames from "classnames";
import BadgesList from "components/BadgesList";
import PaperDebugInfo from "components/PaperDebugInfo/PaperDebugInfo";
import { QuoteSelector } from "components/QuoteSelector";
import StudySnapshot from "components/StudySnapshot";
import { SearchResultTextType } from "helpers/search";
import { getLangDir } from "helpers/language";
import Link from "next/link";
import { SearchResultViewProps } from "../../types";
import PaperTitle from "../shared/PaperTitle";
import SearchResultPaperMetadata from "../shared/PaperMetadata";
import PaperActions from "../shared/PaperActions";
import SearchResultHoverBackground from "../shared/SearchResultHoverBackground";

/**
 * @component SearchResultDefault
 * @description Default detailed view for search results
 */
export const SearchResultDefault = ({
  paper,
  index,
  text,
  textType,
  relativeLink,
  absoluteLink,
  externalLink,
  openDetailsInNewTab,
  onResultClick,
  onClickItem,
  badges,
  extraTags,
  showPaperDoi,
  isProcessingMetadata,
  hideCitations,
  showSourceCheckbox,
  showSaveBtn,
  showCiteBtn,
  showShareBtn,
  showManageUploadedPaperBtn,
  isLoadingExtractedAnswer,
  mixpanelActionSource,
  hashPaperRecordId,
  isUploading,
  isManageDropdownOpen,
  onManageDropdownOpenChange,
  onClickSave,
  onClickCite,
  onCitePaper,
  onFullTextClick,
  onSharePaper,
  researchHubUploadingStateLabels,
  recentlyUpdated,
  recentlyAdded,
  finalRanking,
  usePaperSidebar,
  debugInfo,
  isLoadingFeaturedText,
  directQuotes,
  selectedQuoteIndex,
  setSelectedQuoteIndex,
  isStudySnapshotOpened,
  snapshotEnabled,
  snapshotButton,
  sequence,
  lang,
  ...titleProps
}: SearchResultViewProps) => {
  const { paper_id } = paper;

  const renderFeaturedText = () => {
    return (
      <div className="flex gap-3 my-3">
        {textType === SearchResultTextType.DIRECT_QUOTE &&
          directQuotes &&
          directQuotes.length > 0 && (
            <div className="flex flex-shrink-0 justify-center flex-grow-1 w-[29px]">
              <div className="w-[3px] h-full bg-bgr-subtle rounded-full" />
            </div>
          )}
        <div className="flex flex-col gap-2">
          {textType === SearchResultTextType.DIRECT_QUOTE && directQuotes && (
            <QuoteSelector
              quotes={directQuotes}
              selectedQuote={selectedQuoteIndex}
              onSelectQuote={setSelectedQuoteIndex!}
              citation={titleProps.citation}
            />
          )}
          <span
            className={classNames("sm-normal text-fg-base", {
              "line-clamp-3": textType === SearchResultTextType.DIRECT_QUOTE,
            })}
          >
            <span className="uppercase text-fg-muted tiny-mono-semibold">
              {textType === SearchResultTextType.DIRECT_QUOTE &&
              directQuotes &&
              selectedQuoteIndex !== undefined &&
              directQuotes[selectedQuoteIndex]
                ? directQuotes[selectedQuoteIndex]?.section || "QUOTE"
                : textType === SearchResultTextType.ANSWER
                  ? "ANSWER"
                  : "KEY TAKEAWAY"}
            </span>
            <span className="mx-2 text-fg-faint">·</span>
            {textType === SearchResultTextType.DIRECT_QUOTE &&
            directQuotes &&
            selectedQuoteIndex !== undefined &&
            directQuotes[selectedQuoteIndex]
              ? directQuotes[selectedQuoteIndex].quote
              : text}
          </span>
        </div>
      </div>
    );
  };

  const LEGACY_renderSnapshot = () => {
    return (
      <div className="sparkle-box">
        <div
          className={"order-2 my-3 base-normal text-fg-base leading-[1.4]"}
          dir={getLangDir(lang)}
        >
          {/* Use min-h-[69px] to match the height of the answer text with two lines and loading state to avoid layout shift */}
          <div className="flex items-center gap-4 pl-4 pr-1.5 py-3 min-h-[69px]">
            {!isLoadingFeaturedText ? (
              <h3
                dangerouslySetInnerHTML={{ __html: text }}
                className="flex-1"
                data-intercom-target={`${sequence} result answer`}
              />
            ) : (
              <h3 className="flex flex-col flex-1 gap-3 animate-pulse">
                <div className="bg-bg-subtle animate-pulse h-3 w-[98%] rounded-xl" />
                <div className="bg-bg-subtle animate-pulse h-3 w-[37%] rounded-xl" />
              </h3>
            )}
            <div
              className="inline-flex items-center"
              onMouseDown={(e) => {
                //Prevent result click event
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              {snapshotButton}
            </div>
          </div>
          <div data-intercom-target="snapshot element">
            {isStudySnapshotOpened && snapshotEnabled && (
              <div
                data-testid="snapshot-expanded-widget"
                className="border-t pl-3"
              >
                <StudySnapshot paperId={paper_id} badges={paper?.badges} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const hideStudySnapshot = usePaperSidebar;
  const hideFeaturedText = !usePaperSidebar;

  return (
    <div
      className={classNames(
        "relative w-full transition-colors duration-200 group @container",
        {
          "deep-search-yellow-static":
            recentlyUpdated && !recentlyAdded && !finalRanking,
          "deep-search-yellow": recentlyAdded,
          "deep-search-green": finalRanking,
        }
      )}
    >
      <SearchResultHoverBackground />
      <div
        className={classNames(
          "relative border-b scroll-mt-header cursor-pointer pt-6 pb-[14px]"
        )}
        data-intercom-target={`${sequence} result tile`}
        onClick={onResultClick}
        onMouseDown={(e) => {
          // Fire click events for left or middle mouse button clicks.
          if (e.button === 0 || e.button === 1)
            onClickItem?.(paper, index, textType);
        }}
      >
        <div className="flex-1">
          <Link
            href={relativeLink}
            target={externalLink || openDetailsInNewTab ? "_blank" : undefined}
            key={paper_id}
            data-testid="search-result"
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              if (usePaperSidebar) {
                e.preventDefault();
              }
            }}
            data-test-text={absoluteLink}
          >
            <section>
              <PaperTitle {...titleProps} paper={paper} />
              {!hideFeaturedText && renderFeaturedText()}
              {!hideStudySnapshot && LEGACY_renderSnapshot()}
              <BadgesList badges={badges} extraTags={extraTags} />
            </section>
          </Link>
          <section className="w-full">
            <div className="flex items-center justify-between w-full min-h-9 gap-4 mt-3">
              <SearchResultPaperMetadata
                paper={paper}
                badges={badges}
                extraTags={extraTags}
                showPaperDoi={showPaperDoi}
                isProcessingMetadata={isProcessingMetadata}
                hideCitations={hideCitations}
                resultFormat={titleProps.resultFormat}
                isUploadingLabels={researchHubUploadingStateLabels}
                showSourceCheckbox={showSourceCheckbox}
              />
              <PaperActions
                paper={paper}
                text={text}
                textType={textType}
                shareLink={absoluteLink}
                index={index}
                showSaveBtn={showSaveBtn}
                showCiteBtn={showCiteBtn}
                showShareBtn={showShareBtn}
                showManageUploadedPaperBtn={showManageUploadedPaperBtn}
                isLoadingExtractedAnswer={isLoadingExtractedAnswer}
                resultFormat={titleProps.resultFormat}
                mixpanelActionSource={mixpanelActionSource}
                hashPaperRecordId={hashPaperRecordId}
                isUploading={isUploading}
                isManageDropdownOpen={isManageDropdownOpen}
                onManageDropdownOpenChange={onManageDropdownOpenChange}
                onClickSave={onClickSave}
                onClickCite={onClickCite}
                onCitePaper={onCitePaper}
                onFullTextClick={onFullTextClick}
                onSharePaper={onSharePaper}
              />
            </div>
          </section>
          {debugInfo && <PaperDebugInfo debugInfo={debugInfo} />}
        </div>
      </div>
    </div>
  );
};

export default SearchResultDefault;
